import FontSize from 'client/components/core/Sidebar/components/Settings/components/Font/FontSize'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { PaymentMethodInterface } from 'common/types/entities/PaymentMethodInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import TextFontUpdater from '../../../components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import LineHeight from '../../../components/core/Sidebar/components/Settings/styles/LineHeight'

interface PaymentMethodSettingsProps {
  entity: PaymentMethodInterface
  defaultEntity?: PaymentMethodInterface
}

function PaymentMethodSettings({
  entity,
  defaultEntity,
}: PaymentMethodSettingsProps) {
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const textColor = usePage(pageSelectors.getGlobalTextColor)
  const updateEntity = useUpdateEntity<PaymentMethodInterface>()
  return (
    <>
      <GroupTitle
        isFirst
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'fontSize',
                'fontFileId',
                'fontFamily',
                'fontWeight',
                'fontStyle',
                'lineHeight',
              ]}
              mobileKeys={[
                'mobileFontSize',
                'mobileFontFileId',
                'mobileFontFamily',
                'mobileFontWeight',
                'mobileFontStyle',
                'mobileLineHeight',
              ]}
            />
          )
        }
      >
        page_settings.typography.text_font_size.label
      </GroupTitle>
      <FontSize
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        onChange={updateProp('fontSize')}
        onChangeMobile={updateProp('mobileFontSize')}
        label={'settings_styles.font_size.label'}
      />
      <LineHeight
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
      />
      <TextFontUpdater<PaymentMethodInterface>
        entity={entity}
        update={updateEntity}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={['color']}
              mobileKeys={['mobileColor']}
            />
          )
        }
      >
        color
      </GroupTitle>
      <ColorPickerControl
        label="global_settings.text_color.label"
        color={entity.color || textColor}
        mobileColor={entity.mobileColor}
        update={updateProp('color')}
        mobileUpdate={updateProp('mobileColor')}
        isClearable
      />
      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default PaymentMethodSettings
